import { Injectable, inject, signal, computed } from "@angular/core";
import { PermissionsService } from "./permissions.service";
import { Auth } from "@angular/fire/auth";
import { UserRole } from "../models/permissions.model";

interface UserPermissions {
	global: {
		hasAccess: boolean;
		role?: UserRole;
		data?: any;
	};
	organizations: {
		[organizationId: string]: {
			hasAccess: boolean;
			role?: UserRole;
			data?: any;
		};
	};
	partners: {
		[partnerId: string]: {
			hasAccess: boolean;
			role?: UserRole;
			data?: any;
		};
	};
	globalAccess: {
		hasAccess: boolean;
	};
}

@Injectable({
	providedIn: "root",
})
export class PermissionStateService {
	private permissionsService = inject(PermissionsService);
	private auth = inject(Auth);

	// State signals
	private permissionsState = signal<UserPermissions>({
		global: { hasAccess: false },
		organizations: {},
		partners: {},
		globalAccess: { hasAccess: false },
	});

	// Computed signals for quick access
	isGlobalAdmin = computed(() => this.permissionsState().global.hasAccess);
	hasGlobalAccess = computed(() => this.permissionsState().global.hasAccess);
	hasAnyAccess = computed(() => this.permissionsState().globalAccess.hasAccess);

	// Methods to check specific permissions
	hasOrganizationAccess(organizationId: string) {
		return computed(
			() => this.permissionsState().organizations[organizationId]?.hasAccess ?? false,
		);
	}

	hasPartnerAccess(partnerId: string) {
		return computed(() => this.permissionsState().partners[partnerId]?.hasAccess ?? false);
	}

	getOrganizationRole(organizationId: string) {
		return computed(() => this.permissionsState().organizations[organizationId]?.role);
	}

	getPartnerRole(partnerId: string) {
		return computed(() => this.permissionsState().partners[partnerId]?.role);
	}

	// Methods to update state
	async loadGlobalPermissions() {
		const user = this.auth.currentUser;
		if (!user) return;

		const [globalResult, anyAccessResult] = await Promise.all([
			this.permissionsService.getGlobalPermissions(user.uid),
			this.permissionsService.hasAnyAccess(user.uid),
		]);

		this.permissionsState.update((state) => ({
			...state,
			global: globalResult,
			globalAccess: anyAccessResult,
		}));
	}

	async loadAllOrganizationPermissions() {
		const user = this.auth.currentUser;
		if (!user) return;

		const [someAccess, allAccess] = await Promise.all([
			this.permissionsService.hasSomeOrganizationAccess(user.uid),
			this.permissionsService.getAllOrganizationAccess(user.uid),
		]);

		const organizations = allAccess.reduce(
			(acc, org) => ({
				...acc,
				[org.data.organization_id]: org,
			}),
			{},
		);

		this.permissionsState.update((state) => ({
			...state,
			organizations,
			globalAccess: someAccess.hasAccess ? someAccess : state.globalAccess,
		}));
	}

	async loadAllPartnerPermissions() {
		const user = this.auth.currentUser;
		if (!user) return;

		const [someAccess, allAccess] = await Promise.all([
			this.permissionsService.hasSomePartnerAccess(user.uid),
			this.permissionsService.getAllPartnerAccess(user.uid),
		]);

		const partners = allAccess.reduce(
			(acc, partner) => ({
				...acc,
				[partner.data.partner_id]: partner,
			}),
			{},
		);

		this.permissionsState.update((state) => ({
			...state,
			partners,
			globalAccess: someAccess.hasAccess ? someAccess : state.globalAccess,
		}));
	}

	// Load all permissions at once
	async loadAllPermissions() {
		await Promise.all([
			this.loadGlobalPermissions(),
			this.loadAllOrganizationPermissions(),
			this.loadAllPartnerPermissions(),
		]);
	}

	// Method to check if user has specific role in organization
	hasOrganizationRole(organizationId: string, role: UserRole) {
		return computed(() => this.permissionsState().organizations[organizationId]?.role === role);
	}

	// Method to check if user has specific role in partner
	hasPartnerRole(partnerId: string, role: UserRole) {
		return computed(() => this.permissionsState().partners[partnerId]?.role === role);
	}

	// Clear state
	clearState() {
		this.permissionsState.set({
			global: { hasAccess: false },
			organizations: {},
			partners: {},
			globalAccess: { hasAccess: false },
		});
	}
}
